import { FC } from 'react';
import {
  ErrorInfo,
  ErrorNavigation as ErrorNavigationType,
} from '@agilelab/plugin-wb-governance-common';
import {
  CopyButton,
  renderMessage,
  WbCardContent,
  WbWidget,
} from '@agilelab/plugin-wb-platform';
import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { ErrorLink } from './ErrorLink';
import { ErrorNavigation } from './ErrorNavigation';

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  endAdornment: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: 1,
    gap: theme.spacing(2),
    overflowY: 'auto',
  },
  errorContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flex: '1 1 0',
  },
  errorColumn: {
    flex: 0.5,
    height: '100%',
  },
}));

const styles = {
  button: {
    minWidth: 0,
  },
  icon: {
    width: '22px',
    height: '22px',
  },
};

interface ErrorBoxProps {
  error: ErrorInfo;
  headerTitle: string;
  handleGoToLine?: (line: number, id: string) => void;
  navigation?: ErrorNavigationType;
  policyEditorId?: string;
  descriptorEditorId?: string;
}

export const ErrorBox: FC<ErrorBoxProps> = ({
  handleGoToLine,
  error,
  navigation,
  policyEditorId,
  descriptorEditorId,
}) => {
  const classes = useStyles();

  return (
    <WbWidget
      title="Error info"
      actions={
        <Box className={classes.header}>
          <Box className={classes.endAdornment}>
            {!!navigation && (
              <ErrorNavigation
                current={navigation.current}
                total={navigation.total}
                handleSwitch={navigation.handleSwitch}
              />
            )}
            <CopyButton
              text={error.message}
              hideIcon
              size="small"
              color="secondary"
              variant="text"
              style={styles.button}
            >
              <FileCopyIcon style={styles.icon} />
            </CopyButton>
          </Box>
        </Box>
      }
    >
      <Box className={classes.body}>
        <WbCardContent>
          <Typography component="div">
            {renderMessage(error.message)}
          </Typography>
          <Box className={classes.errorContainer}>
            <Box
              role="region"
              className={classes.errorColumn}
              aria-label="policy-errors"
            >
              {error.policyErrorRows?.map((errorRow: number, idx: number) => {
                return (
                  <ErrorLink
                    key={`policy-${errorRow}-${idx}`}
                    title={`Policy:${errorRow}`}
                    onClick={_e => {
                      if (handleGoToLine) {
                        handleGoToLine(errorRow, policyEditorId || 'policy');
                      }
                    }}
                  />
                );
              })}
            </Box>

            <Box
              role="region"
              className={classes.errorColumn}
              aria-label="descriptor-errors"
            >
              {error.descriptorErrorRows?.map(
                (errorRow: number, idx: number) => {
                  return (
                    <ErrorLink
                      key={`descriptor-${errorRow}-${idx}`}
                      title={`Descriptor:${errorRow}`}
                      onClick={_e => {
                        if (handleGoToLine) {
                          handleGoToLine(
                            errorRow,
                            descriptorEditorId || 'descriptor',
                          );
                        }
                      }}
                    />
                  );
                },
              )}
            </Box>
          </Box>
        </WbCardContent>
      </Box>
    </WbWidget>
  );
};
